import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Collapse from '@mui/material/Collapse';
import css from './ExpandablePanel.css';

const ExpandablePanel = ({
    expanded = false,
    collapsedContent = null,
    header = null,
    disabled = false,
    className = null,
    subHeader = null,
    isSticky = false,
    isExpandable = true,
    headerContainerClassName = null,
    dataTestId,
    overrideClasses = {},
    id = '',
    onExpandClick = () => {},
    animate = true,
    content = null,
    footer = null,
    children,
}) => {
    const handleExpandClick = () => {
        if (disabled || !isExpandable) {
            return;
        }
        onExpandClick(id);
    };

    const getClass = cssClassName => {
        return [css[cssClassName], overrideClasses[cssClassName]];
    };

    const getContentArea = () => {
        let contentElem = (
            <div className={classNames(...getClass('contentArea'))}>
                <div key="content">{content || children}</div>
                <div key="footer">{footer}</div>
            </div>
        );

        if (animate) {
            contentElem = (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {contentElem}
                </Collapse>
            );
        } else if (!expanded) {
            contentElem = null;
        }
        return (
            <React.Fragment>
                {contentElem}
                {!animate && !expanded && collapsedContent ? collapsedContent : null}
            </React.Fragment>
        );
    };

    return (
        <div
            className={classNames(...getClass('container'), {
                [css.expanded]: expanded,
                [css.hasFooter]: collapsedContent && !expanded,
                [css.disabled]: disabled,
                [css.notExpandable]: !isExpandable,
                [className]: className,
            })}
            data-testid={dataTestId}
        >
            <div className={classNames(css.headerStickinessContainer, { [css.sticky]: isSticky })}>
                <div
                    className={classNames(...getClass('headerContainer'), {
                        [headerContainerClassName]: headerContainerClassName,
                        [overrideClasses.headerContainerExpanded]: expanded && overrideClasses?.headerContainerExpanded,
                    })}
                    data-testid={`${dataTestId}Inner`}
                    onClick={handleExpandClick}
                    role="button"
                >
                    <div className={classNames(...getClass('arrowArea'))}>
                        <div className={css.arrow} />
                    </div>
                    <div className={classNames(...getClass('externalHeaderArea'))}>{header}</div>
                </div>
                {subHeader}
            </div>
            {getContentArea()}
        </div>
    );
};

ExpandablePanel.propTypes = {
    expanded: PropTypes.bool,
    onExpandClick: PropTypes.func,
    content: PropTypes.element,
    collapsedContent: PropTypes.element,
    footer: PropTypes.element,
    header: PropTypes.element,
    id: PropTypes.string,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    headerContainerClassName: PropTypes.string,
    subHeader: PropTypes.element,
    overrideClasses: PropTypes.objectOf(PropTypes.any),
    animate: PropTypes.bool,
    isSticky: PropTypes.bool,
    isExpandable: PropTypes.bool,
    dataTestId: PropTypes.string,
};

export default ExpandablePanel;
